import { DateConstants } from "utils/constants";
import { DateHelper } from "utils/helpers";

const MonthsPerQuarter = 3;

/**
 * A revenue-reporting period is calendar aligned and spans monthCount months starting at
 * periodMonth, so the todo has to derive its own span: the todo detail carries the period's
 * first month plus how many months it covers.
 */
export interface RevenueReportingPeriod {
  firstDay: Date;
  lastDay: Date;
}

/**
 * The period fields of a revenue-report todo detail. A weekly template puts several periods in
 * one month, so year + month no longer identify a period on their own: periodStartDay completes
 * the identity and periodDayCount is the window length (0 for whole calendar months).
 */
export interface RevenueReportingPeriodFields {
  periodYear?: number;
  periodMonth?: number;
  periodMonthCount?: number;
  periodStartDay?: number;
  periodDayCount?: number;
  periodIsoWeek?: number;
  periodIsoWeekYear?: number;
}

const normalizeMonthCount = (periodMonthCount?: number) =>
  periodMonthCount && periodMonthCount > 0 ? periodMonthCount : 1;

export const isDayWindow = (fields?: RevenueReportingPeriodFields) => (fields?.periodDayCount ?? 0) > 0;

export const getRevenueReportingPeriod = (fields?: RevenueReportingPeriodFields): RevenueReportingPeriod => {
  const { periodYear, periodMonth, periodMonthCount, periodStartDay, periodDayCount } = fields ?? {};

  if (isDayWindow(fields)) {
    const firstDay = new Date(periodYear ?? 0, (periodMonth ?? 1) - 1, periodStartDay ?? 1);
    const lastDay = new Date(
      firstDay.getFullYear(),
      firstDay.getMonth(),
      firstDay.getDate() + (periodDayCount ?? 1) - 1
    );
    return { firstDay, lastDay };
  }

  const monthCount = normalizeMonthCount(periodMonthCount);
  const firstDay = DateHelper.getDateFromYearMonth((periodYear ?? 0) * 100 + (periodMonth ?? 0));
  const lastMonth = new Date(firstDay.getFullYear(), firstDay.getMonth() + monthCount - 1, 1);
  return { firstDay, lastDay: DateHelper.getLastDayOfMonth(lastMonth) };
};

const isCalendarQuarter = (firstDay: Date) => firstDay.getMonth() % MonthsPerQuarter === 0;

/**
 * Labels the period the way the customer thinks about it: the span of days for a weekly template,
 * a month name for monthly, Q1-Q4 for a calendar quarter and a month range for everything else.
 *
 * A day window is named by its own dates rather than by an ISO week. The weekly grid is anchored
 * on the template's start date instead of on Monday, so a window can straddle two ISO weeks and a
 * week number names it after only the part holding most of its days. The range is inclusive of
 * lastDay, so a seven-day window reads "11 aug - 17 aug".
 *
 * A yearly period is named by its span rather than by a single year. It is twelve months anchored
 * on the template's start month, so it can straddle the calendar year, and "2026" for a period
 * running September 2025 to August 2026 reads as calendar 2026. Heart and the service name it the
 * same way, and the two sit on the same screen for an accountant.
 *
 * Only a span starting in January, April, July or October is a quarter. Quarterly periods are
 * pinned to the calendar so this always holds, but an off-grid three-month span must not be
 * labelled with a quarter it is not.
 */
export const formatRevenueReportingPeriod = (fields?: RevenueReportingPeriodFields): string => {
  const { periodMonthCount } = fields ?? {};

  if (isDayWindow(fields)) {
    const { firstDay, lastDay } = getRevenueReportingPeriod(fields);
    return DateHelper.formatDateRange(firstDay, lastDay, "-", DateConstants.DateMonthLongConfig);
  }

  const monthCount = normalizeMonthCount(periodMonthCount);
  const { firstDay, lastDay } = getRevenueReportingPeriod(fields);

  if (monthCount === MonthsPerQuarter && isCalendarQuarter(firstDay)) {
    return `Q${Math.floor(firstDay.getMonth() / MonthsPerQuarter) + 1} ${firstDay.getFullYear()}`;
  }
  if (monthCount > 1) {
    return DateHelper.formatMonthRange(firstDay, lastDay);
  }
  return DateHelper.formatMonth(firstDay);
};
